import type { AfterViewInit, OnInit } from '@angular/core';
import {
  Component,
  DestroyRef,
  Injector,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl, Validators } from '@angular/forms';
import type { FormField } from '@angular/forms/signals';
import { FORM_FIELD } from '@angular/forms/signals';

@Component({
  selector: 'onyx-base-form-control',
  imports: [],
  template: ``,
  styles: ``,
})
export class OnyxBaseFormControlComponent implements OnInit, AfterViewInit {
  protected readonly injector = inject(Injector);
  protected readonly destroyRef = inject(DestroyRef);

  public readonly disabled = model(false);
  public readonly readonly = input(false);
  public readonly hidden = input(false);
  public readonly touched = input(false);
  public readonly dirty = input(false);
  public readonly required = input(false);

  public readonly touch = output<void>();

  protected formField_ = signal<FormField<unknown> | null>(null);
  protected get formField() {
    return this.formField_.asReadonly();
  }

  private formControl_ = signal<NgControl | null>(null);
  protected get formControl() {
    return this.formControl_.asReadonly();
  }

  private isInvalid_ = signal(false);

  protected isInvalid = computed(() => {
    const formField = this.formField();
    if (!formField) return this.isInvalid_();

    const state = formField.state();
    return this.resolveIsInvalid({
      invalid: state.invalid(),
      dirty: state.dirty(),
      touched: state.touched(),
    });
  });
  protected isValid = computed(() => !this.isInvalid());
  protected isOptional = computed(() => {
    if (this.required()) return false;

    const formField = this.formField();
    if (formField?.state().required()) return false;

    const control = this.formControl()?.control;
    if (
      control?.hasValidator(Validators.required) ||
      control?.hasValidator(Validators.requiredTrue)
    ) {
      return false;
    }

    return true;
  });

  public ngOnInit(): void {
    this.formField_.set(
      this.injector.get(FORM_FIELD, null, {
        optional: true,
        self: true,
      }),
    );

    this.formControl_.set(
      this.injector.get(NgControl, null, {
        optional: true,
        self: true,
      }),
    );
  }

  public ngAfterViewInit(): void {
    if (this.formField()) return;
    if (!this.formControl_()) return;

    const statusChanges$ = this.formControl_()?.statusChanges?.pipe(
      takeUntilDestroyed(this.destroyRef),
    );
    const valueChanges$ = this.formControl_()?.valueChanges?.pipe(
      takeUntilDestroyed(this.destroyRef),
    );

    this.update();
    statusChanges$?.subscribe(() => this.update());
    valueChanges$?.subscribe(() => this.update());
  }

  private update(): void {
    this.isInvalid_.set(this.getIsInvalid());
  }

  private getIsInvalid(): boolean {
    const control = this.formControl()?.control;
    if (!control) return false;

    const { invalid, dirty, touched } = control;
    return this.resolveIsInvalid({
      invalid,
      dirty,
      touched,
    });
  }

  private resolveIsInvalid(state: {
    invalid: boolean;
    dirty: boolean;
    touched: boolean;
  }): boolean {
    const isDirty = state.dirty && state.touched;
    return state.invalid && isDirty;
  }
}
